/**
 * The client-owned `NewsletterDraft` model for the v2 wizard.
 *
 * Everything the pre-signup flow collects lives here as plain, serialisable data
 * plus pure transition helpers, so the logic is unit-testable in isolation and
 * the component stays thin. The draft is mirrored to localStorage (see
 * `./storage`) on every change, and claimed to a real newsletter after signup in
 * a later stage. Transitions never touch `updatedAt`; the storage layer stamps
 * it at write time so the transitions stay pure and deterministic.
 */
import { createId, validateSubdomain } from '@nuff/core';
import type {
  LikedNewsletter as PersistedLikedNewsletter,
  NewsletterInput,
  NewsletterPurpose,
  NewsletterVoice,
} from '@nuff/types';
import type { AudienceType } from '../suggestions/provider';

/** Who the newsletter is for. `other` carries free text but still seeds an audience. */
export type DraftPurpose =
  | { readonly kind: 'company' }
  | { readonly kind: 'community' }
  | { readonly kind: 'other'; readonly customText: string };

/** The chosen voice: a named preset, or a pasted sample that wins over presets. */
export type DraftVoice =
  | { readonly kind: 'preset'; readonly id: string; readonly label: string }
  | { readonly kind: 'custom'; readonly text: string };

/** A newsletter the user likes: a preset pick or a pasted link. */
export interface LikedNewsletter {
  readonly id?: string;
  readonly label: string;
  readonly url: string;
}

/** The default voice, pre-selected so the Voice step is always satisfiable. */
export const DEFAULT_VOICE: DraftVoice = {
  kind: 'preset',
  id: 'warm',
  label: 'Warm and plain-spoken',
};

export const MAX_FOCUS = 3;
export const MAX_FOCUS_LABEL = 60;
export const MAX_NAME = 120;
export const MAX_CUSTOM_VOICE = 2000;
export const MAX_LIKED = 10;

/** The complete pre-auth draft. `version` guards against stale-shape resumes. */
export interface NewsletterDraft {
  readonly draftId: string;
  readonly version: 1;
  readonly purpose: DraftPurpose;
  readonly focus: readonly string[];
  readonly voice: DraftVoice;
  readonly name: string;
  readonly tagline: string;
  readonly subdomain: string;
  readonly likedNewsletters: readonly LikedNewsletter[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

export const DRAFT_VERSION = 1 as const;

/** A fresh draft. `purpose` defaults to community (see the design blueprint). */
export function createDraft(now: string = new Date().toISOString()): NewsletterDraft {
  return {
    draftId: createId('draft'),
    version: DRAFT_VERSION,
    purpose: { kind: 'community' },
    focus: [],
    voice: DEFAULT_VOICE,
    name: '',
    tagline: '',
    subdomain: '',
    likedNewsletters: [],
    createdAt: now,
    updatedAt: now,
  };
}

/* --------------------------------------------------------- step ordering -- */

/** Screen order. `welcome` is first; `building` is the terminal signup gate. */
export const STEP = {
  welcome: 0,
  purpose: 1,
  focus: 2,
  voice: 3,
  name: 4,
  address: 5,
  liked: 6,
  building: 7,
} as const;

export type StepId = keyof typeof STEP;

export const STEPS: readonly { readonly id: StepId; readonly label: string }[] = [
  { id: 'welcome', label: 'Welcome' },
  { id: 'purpose', label: 'Purpose' },
  { id: 'focus', label: 'Focus' },
  { id: 'voice', label: 'Voice' },
  { id: 'name', label: 'Name' },
  { id: 'address', label: 'Address' },
  { id: 'liked', label: 'Newsletters' },
  { id: 'building', label: 'Finish' },
];

export const LAST_STEP: number = STEP.building;

/* ---------------------------------------------------------- derivations -- */

const COMPANY_KEYWORDS = [
  'company',
  'brand',
  'product',
  'products',
  'customer',
  'customers',
  'client',
  'clients',
  'marketing',
  'launch',
  'business',
  'startup',
  'saas',
  'agency',
];

/** Map a purpose to the two-way audience split that seeds suggestions. */
export function audienceFor(draft: NewsletterDraft): AudienceType {
  const { purpose } = draft;
  if (purpose.kind === 'company') {
    return 'company';
  }
  if (purpose.kind === 'community') {
    return 'community';
  }
  const text = purpose.customText.toLowerCase();
  return COMPANY_KEYWORDS.some((word) => text.includes(word)) ? 'company' : 'community';
}

/** The seed topic string handed to the suggestion provider. */
export function seedTopic(draft: NewsletterDraft): string {
  return draft.focus.join(', ');
}

/** A signature over the seed inputs; when it changes, suggestions regenerate. */
export function seedSignature(draft: NewsletterDraft): string {
  const voiceLabel = draft.voice.kind === 'preset' ? draft.voice.label : 'custom';
  return `${draft.focus.join('|').toLowerCase()}::${audienceFor(draft)}::${voiceLabel}`;
}

/* ----------------------------------------------------------- transitions -- */

export function setPurposeKind(
  draft: NewsletterDraft,
  kind: DraftPurpose['kind'],
): NewsletterDraft {
  if (kind === 'other') {
    const customText = draft.purpose.kind === 'other' ? draft.purpose.customText : '';
    return { ...draft, purpose: { kind: 'other', customText } };
  }
  return { ...draft, purpose: { kind } };
}

export function setPurposeText(draft: NewsletterDraft, customText: string): NewsletterDraft {
  return { ...draft, purpose: { kind: 'other', customText } };
}

function cleanFocus(topic: string): string {
  return topic.trim().replace(/\s+/g, ' ').slice(0, MAX_FOCUS_LABEL);
}

function hasFocus(focus: readonly string[], topic: string): boolean {
  const lower = topic.toLowerCase();
  return focus.some((item) => item.toLowerCase() === lower);
}

/** Toggle a focus chip: remove if present, else append (capped at MAX_FOCUS). */
export function toggleFocus(draft: NewsletterDraft, topic: string): NewsletterDraft {
  const clean = cleanFocus(topic);
  if (clean.length === 0) {
    return draft;
  }
  if (hasFocus(draft.focus, clean)) {
    return {
      ...draft,
      focus: draft.focus.filter((item) => item.toLowerCase() !== clean.toLowerCase()),
    };
  }
  if (draft.focus.length >= MAX_FOCUS) {
    return draft;
  }
  return { ...draft, focus: [...draft.focus, clean] };
}

/** Add a typed focus topic to the front (capped, de-duplicated). */
export function addFocus(draft: NewsletterDraft, topic: string): NewsletterDraft {
  const clean = cleanFocus(topic);
  if (clean.length === 0 || hasFocus(draft.focus, clean) || draft.focus.length >= MAX_FOCUS) {
    return draft;
  }
  return { ...draft, focus: [clean, ...draft.focus] };
}

export function setVoicePreset(
  draft: NewsletterDraft,
  preset: { id: string; label: string },
): NewsletterDraft {
  return { ...draft, voice: { kind: 'preset', id: preset.id, label: preset.label } };
}

/**
 * Set the pasted custom voice. A non-empty value wins over the presets; clearing
 * it restores `fallback` (the last-selected preset, tracked by the component).
 */
export function setCustomVoice(
  draft: NewsletterDraft,
  text: string,
  fallback: { id: string; label: string } = { id: 'warm', label: 'Warm and plain-spoken' },
): NewsletterDraft {
  if (text.trim().length === 0) {
    return { ...draft, voice: { kind: 'preset', id: fallback.id, label: fallback.label } };
  }
  return { ...draft, voice: { kind: 'custom', text: text.slice(0, MAX_CUSTOM_VOICE) } };
}

export function setName(draft: NewsletterDraft, name: string, tagline: string): NewsletterDraft {
  return { ...draft, name: name.slice(0, MAX_NAME), tagline };
}

export function setSubdomain(draft: NewsletterDraft, subdomain: string): NewsletterDraft {
  return { ...draft, subdomain: subdomain.trim().toLowerCase() };
}

/** Map the draft's purpose to the persisted shape (free text folded into `text`). */
function purposeForInput(draft: NewsletterDraft): NewsletterPurpose {
  const { purpose } = draft;
  if (purpose.kind === 'other') {
    const text = purpose.customText.trim();
    return text.length > 0 ? { kind: 'other', text } : { kind: 'other' };
  }
  return { kind: purpose.kind };
}

/** Map the draft's voice to the persisted shape (drop the preset's display label). */
function voiceForInput(draft: NewsletterDraft): NewsletterVoice {
  return draft.voice.kind === 'preset'
    ? { kind: 'preset', id: draft.voice.id }
    : { kind: 'custom', text: draft.voice.text };
}

/**
 * Convert a saved draft into the API's newsletter-create input, used to claim
 * the draft as a real newsletter right after signup. Returns null when there is
 * nothing worth creating yet (no name). A subdomain is only included when it is
 * still valid; an invalid/empty one is dropped so the newsletter is created
 * without an address rather than rejected outright.
 *
 * The whole wizard config rides along so source discovery has more than a raw
 * topic string to work with: the tagline, purpose, the derived audience, the
 * chosen voice, and the liked newsletters (capped in the draft). The custom
 * voice sample is persisted but is never fed to the discovery planner.
 */
export function draftToNewsletterInput(draft: NewsletterDraft): NewsletterInput | null {
  const name = draft.name.trim();
  if (name.length === 0) {
    return null;
  }
  // Carry the focus topics through so source discovery has something to search.
  const topics = draft.focus.filter((topic) => topic.trim().length > 0);
  const liked: PersistedLikedNewsletter[] = draft.likedNewsletters.map((item) => ({
    label: item.label,
    url: item.url,
  }));
  const subdomain = draft.subdomain.trim();
  const validated = subdomain.length > 0 ? validateSubdomain(subdomain) : null;
  const tagline = draft.tagline.trim();
  const input: NewsletterInput = {
    name,
    purpose: purposeForInput(draft),
    audience: audienceFor(draft),
    voice: voiceForInput(draft),
    // Carry EVERY field the draft holds. The tagline was previously dropped here,
    // so the name step's chosen tagline never reached persistence; include it now.
    ...(tagline.length > 0 ? { tagline } : {}),
    ...(topics.length > 0 ? { topics } : {}),
    ...(liked.length > 0 ? { likedNewsletters: liked } : {}),
    ...(validated?.ok ? { subdomain: validated.value } : {}),
  };
  return input;
}

/** Best-effort friendly label for a pasted URL (host, minus a leading www). */
export function labelForUrl(url: string): string {
  const clean = url.trim();
  const withProtocol = /^https?:\/\//i.test(clean) ? clean : `https://${clean}`;
  try {
    return new URL(withProtocol).hostname.replace(/^www\./, '');
  } catch {
    return clean;
  }
}

/** Parse a pasted newsletter link leniently. Returns null for obvious junk. */
export function parseLink(input: string): { label: string; url: string } | null {
  const clean = input.trim();
  if (clean.length === 0) {
    return null;
  }
  const withProtocol = /^https?:\/\//i.test(clean) ? clean : `https://${clean}`;
  try {
    const parsed = new URL(withProtocol);
    if (!parsed.hostname.includes('.')) {
      return null;
    }
    return { label: labelForUrl(clean), url: parsed.toString() };
  } catch {
    return null;
  }
}

/** Add a liked newsletter (capped, de-duplicated by url). */
export function addLiked(draft: NewsletterDraft, liked: LikedNewsletter): NewsletterDraft {
  if (draft.likedNewsletters.length >= MAX_LIKED) {
    return draft;
  }
  if (draft.likedNewsletters.some((item) => item.url === liked.url)) {
    return draft;
  }
  return { ...draft, likedNewsletters: [...draft.likedNewsletters, liked] };
}

export function removeLiked(draft: NewsletterDraft, url: string): NewsletterDraft {
  return {
    ...draft,
    likedNewsletters: draft.likedNewsletters.filter((item) => item.url !== url),
  };
}

export function isLiked(draft: NewsletterDraft, url: string): boolean {
  return draft.likedNewsletters.some((item) => item.url === url);
}

/* ------------------------------------------------- subdomain candidates -- */

/** Slugify a name into a subdomain-safe base (lowercase, hyphenated). */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 40)
    .replace(/-$/g, '');
}

/**
 * Generate a few clean, valid `.nuffsaid.cloud` address candidates from a name.
 * Everything is run through `validateSubdomain`, so only usable options survive.
 */
export function subdomainCandidates(name: string): string[] {
  const base = slugify(name);
  const firstWord = base.split('-')[0] ?? '';
  const raw = [
    base,
    firstWord,
    `${base}-news`,
    `the-${base}`,
    `${base}-hq`,
    `${firstWord}-weekly`,
    'my-newsletter',
  ];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const candidate of raw) {
    const result = validateSubdomain(candidate);
    if (result.ok && !seen.has(result.value)) {
      seen.add(result.value);
      out.push(result.value);
    }
    if (out.length >= 5) {
      break;
    }
  }
  return out;
}

/* -------------------------------------------------------------- gating -- */

/** Whether the user may leave `step` given the current draft. */
export function canAdvance(step: number, draft: NewsletterDraft): boolean {
  switch (step) {
    case STEP.focus:
      return draft.focus.length >= 1;
    case STEP.name:
      return draft.name.trim().length > 0;
    case STEP.address:
      return validateSubdomain(draft.subdomain).ok;
    default:
      // welcome, purpose (default community), voice (default preset), liked
      // (optional) are always satisfiable.
      return true;
  }
}

/**
 * The furthest step a resumed draft should open at. Never jumps onto the signup
 * gate (building) and never past the last incomplete decision.
 */
export function furthestStep(draft: NewsletterDraft): number {
  let step = STEP.purpose;
  while (step < STEP.liked && canAdvance(step, draft)) {
    step += 1;
  }
  return step;
}

/** A one-line recap of the draft for the finish screen. */
export function recapLine(draft: NewsletterDraft): string {
  const name = draft.name.trim() || 'Your newsletter';
  const address = draft.subdomain ? `${draft.subdomain}.nuffsaid.cloud` : 'your address';
  return `${name} at ${address}`;
}
